import React, { useCallback, useState } from "react";

import { DownMinimalArrow } from "icons";
import pluralize from "pluralize";

import metaIQApi from "apis/metaIQ";
import {
  DropdownOverlay,
  SubHeader,
  Table,
  Typography,
  Checkbox,
  Pagination,
} from "clearsense-ui/components";
import Filter from "components/Common/FilterHeader/Filter";
import {
  FILTER_CONDITIONS,
  FILTER_OPERATIONS,
} from "components/DataManagement/constants/stewardFilter";
import { useColumnHeaderSelector } from "hooks/useColumnHeaderSelector";
import useSearchAndPaginate from "hooks/useSearchAndPaginate";

import { RESULT_TABLE_COLUMN } from "./constants";

const Results = () => {
  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [filterConditions, setFilterConditions] = useState([]);
  const params = new URLSearchParams(window.location.search);

  const fetchResults = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams(window.location.search);
      const currentPage = params.get("page") || 1;
      const pageSize = params.get("per_page") || 10;
      const response = await metaIQApi.fetchData(
        currentPage,
        pageSize,
        params.get("audit_id")
      );
      setResults(JSON.parse(response.data.results));
      setTotalCount(response.data.count);
    } catch (error) {
      logger.error(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const { currentPage, pageSize, setCurrentPage, setPageSize } =
    useSearchAndPaginate(fetchResults, {
      all: () => setFilterConditions([]),
    });

  const { columns, toggleVisibility } =
    useColumnHeaderSelector(RESULT_TABLE_COLUMN);

  return (
    <div className="cs-ui-container cs-ui-container__table">
      <SubHeader
        className="mt-8"
        breadcrumbs={[
          { label: "Reference Data", link: "#" },
          { label: "Meta IQ", link: "/management/reference-data/meta-iq" },
          { label: `${params.get("name")}: Results`, link: "#" },
        ]}
      />
      <div className="flex flex-col mt-4 space-y-6 h-full">
        <div className="flex justify-between w-full">
          <div className="flex items-center gap-4 flex-nowrap">
            <Typography style="large" type="bold">
              {pluralize("Column", totalCount, true)}
            </Typography>
            <Filter
              title="Classifications"
              filterConditions={filterConditions}
              setFilterConditions={setFilterConditions}
              columnOptions={RESULT_TABLE_COLUMN?.filter(
                column => column.title !== "Action"
              ).map(column => ({ label: column.title, value: column.title }))}
              conditionOptions={FILTER_CONDITIONS}
              filterOperations={FILTER_OPERATIONS}
            />
          </div>

          <DropdownOverlay
            buttonStyle="secondary"
            buttonLabel="Edit Columns"
            buttonProps={{
              icon: DownMinimalArrow,
              iconPosition: "right",
            }}
          >
            <div className="p-4 pb-0">
              <Typography
                style="h6"
                className="mb-2 uppercase text-grey-medium-shade"
              >
                enabled
              </Typography>
              {columns?.selected?.map(column => (
                <Checkbox
                  key={column.key}
                  label={column.title}
                  id={column.key}
                  className="mb-5"
                  onClick={e =>
                    columns.selected.length > 1 && toggleVisibility(e.target.id)
                  }
                  checked
                  readOnly
                  disabled={columns.selected.length === 1}
                />
              ))}
            </div>
            <div className="px-4">
              <Typography
                style="h6"
                className="mb-2 uppercase text-grey-medium-shade"
              >
                disabled
              </Typography>
              {columns?.unSelected?.map(column => (
                <Checkbox
                  key={column.key}
                  label={column.title}
                  id={column.key}
                  className="mb-5"
                  onClick={e => toggleVisibility(e.target.id)}
                  readOnly
                />
              ))}
            </div>
          </DropdownOverlay>
        </div>
        <div className="flex flex-col flex-grow p-2 bg-white">
          <Table
            rowData={results}
            columnData={columns.selected}
            fixedHeight
            allowRowClick={false}
            rowKey={record => record.id}
            isLoading={isLoading}
          />
          <Pagination
            className="p-8 py-2 mt-auto"
            totalRecordsCount={totalCount}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            pageSize={pageSize}
            showResultsPerPage
            setPageSize={setPageSize}
          />
        </div>
      </div>
    </div>
  );
};

export default Results;
